import { useToggle } from '@sparemin/blockhead';
import React from 'react';
import { isFunction, noop } from 'underscore';

import AssetOverlayLabel from 'components/AssetOverlayLabel';
import TextWorkspace from 'components/TextWorkspace';
import {
  DYNAMIC_ASSET_TYPE,
  DYNAMIC_ASSET_VALUE,
} from 'components/VideoTemplateEditor/integrations/useDynamicPodcastTextIntegration';
import {
  getDraggingEnabledByTemplateId,
  getResizableEdgesByTemplateId,
} from 'utils/embed/text-overlay';

import { replaceDynamicKeywordsWithPlaceholders } from 'utils/embed/text-overlay-ts';
import useTextOverlayCaptionsAnimations from '../../hooks/useTextOverlayCaptionsAnimations';
import useTextOverlayGooeyEffect from '../../hooks/useTextOverlayGooeyEffect';
import useOverlayPosition from '../../hooks/useTextOverlayPosition';
import useTextOverlayResizeDelta from '../../hooks/useTextOverlayResizeDelta';
import useTextOverlayStyle from '../../hooks/useTextOverlayStyle';
import { useTextOverlay } from '../../state/TextOverlayProvider';
import { block } from '../../utils';
import TextOverlayTextPreview from '../TextOverlayTextPreview';
import { RESIZE_HANDLES_STYLES } from './constants';

export interface TextOverlayPreviewPaneProps {
  aspectRatio: number;
  background: React.ReactElement | (({ viewport }) => React.ReactElement);
  editable: boolean;
  inputDisabled?: boolean;
  templateId?: string;
  isCaption?: boolean;
}

const TextOverlayPreviewPane: React.FunctionComponent<TextOverlayPreviewPaneProps> = props => {
  const {
    aspectRatio,
    background,
    editable,
    inputDisabled = false,
    templateId,
    isCaption,
  } = props;

  const {
    boxSizeFitCheckerExtension,
    canvasSize,
    draftEditorData,
    onChangeText,
    textValue,
    isDynamicContent,
    isDynamicContentEnabled,
    isDynamicContentAvailable,
    theme,
  } = useTextOverlay();

  const { textAreaStyles, textBuilderStyles } = useTextOverlayStyle();

  const {
    toggleOff: turnOffInputMode,
    toggleOn: turnOnInputMode,
    value: inputModeEnabled,
  } = useToggle(false);

  const {
    onDrag,
    onDragStop,
    onResizeStart,
    onResizeStop,
    position,
    size,
  } = useOverlayPosition();

  const {
    resizeDeltaObserver: boxFitCheckerResizeDeltaObserver,
  } = boxSizeFitCheckerExtension;

  const {
    resizeDeltaObserver: gooeyEffectResizeDeltaObserver,
  } = useTextOverlayGooeyEffect();

  const {
    resizeDeltaObserver: captionsAnimationsResizeDeltaObserver,
  } = useTextOverlayCaptionsAnimations();

  const resizeDeltaObservers = React.useMemo(
    () => [
      boxFitCheckerResizeDeltaObserver,
      gooeyEffectResizeDeltaObserver,
      captionsAnimationsResizeDeltaObserver,
    ],
    [
      boxFitCheckerResizeDeltaObserver,
      captionsAnimationsResizeDeltaObserver,
      gooeyEffectResizeDeltaObserver,
    ],
  );

  const {
    onTextBoxResize,
    onTextBoxResizeEnd,
    onTextBoxResizeStart,
  } = useTextOverlayResizeDelta({
    canvasSize,
    draftEditorData,
    resizeObservers: resizeDeltaObservers,
  });

  const textType = draftEditorData?.get('textType');
  const textHtml = draftEditorData?.get('textHtml');

  const isEpisodeTitle =
    textType === DYNAMIC_ASSET_TYPE.episode ||
    textHtml?.includes(DYNAMIC_ASSET_VALUE.episode);

  const draggingEnabled =
    !inputModeEnabled && getDraggingEnabledByTemplateId(templateId);

  // Replace dynamic keywords with placeholders if text has dynamic content.
  //
  // - `textType` is only set when the user explicitly selected the dynamic asset
  // type via UI.
  //
  // - `textHtml` may contain dynamic assets (like `%%Episode%%`) that were set
  // outside the Advanced Editor (e.g., UCS flow), where `textType` is NOT set.
  // Checking for both `textType` || `textHtml` guarantees that the dynamic asset
  // will be properly formatted regardless of how it was created.
  const formattedTextValue =
    textType || textHtml
      ? replaceDynamicKeywordsWithPlaceholders(textValue)
      : textValue;

  return (
    <TextWorkspace
      aspectRatio={aspectRatio}
      background={
        <div>
          <div className={block('preview-fade')} />
          {isFunction(background)
            ? background({ viewport: canvasSize })
            : background}
        </div>
      }
      boxBorderClassName={block('resize-box-border', {
        'in-edition': inputModeEnabled,
        [theme]: true,
      })}
      className={block('workspace')}
      containerSize={canvasSize}
      defaultSize={canvasSize}
      disabled={inputDisabled}
      draggingEnabled={draggingEnabled}
      onTextBoxDrag={onDrag}
      onTextBoxDragStop={onDragStop}
      onTextBoxResize={onTextBoxResize}
      onTextBoxResizeStart={onTextBoxResizeStart(onResizeStart)}
      onTextBoxResizeStop={onTextBoxResizeEnd(onResizeStop)}
      onWorkspaceSizeChange={noop}
      positionOverlayClassName={block('position-overlay')}
      resizeHandleStyles={
        theme !== 'dynamic' ? RESIZE_HANDLES_STYLES : undefined
      }
      resizingEnabled={!inputModeEnabled}
      textBoxClassName={block('text-box', {
        [theme]: true,
      })}
      textBoxPosition={position}
      textBoxSize={size}
      resizableEdges={getResizableEdgesByTemplateId(templateId)}
    >
      <>
        <TextOverlayTextPreview
          editable={editable}
          inputModeEnabled={inputModeEnabled}
          onChangeText={onChangeText}
          onDisableInputMode={turnOffInputMode}
          onEnableInputMode={turnOnInputMode}
          textAreaStyles={textAreaStyles}
          textBuilderStyles={textBuilderStyles}
          textValue={formattedTextValue}
          isCaption={isCaption}
        />

        {isDynamicContent && isDynamicContentEnabled && (
          <AssetOverlayLabel
            label={isEpisodeTitle ? 'episode title' : 'podcast title'}
            isAvailable={isDynamicContentAvailable}
            className={block('overlay-label')}
          />
        )}
      </>
    </TextWorkspace>
  );
};

export default TextOverlayPreviewPane;
